import React from 'react';
import {Toolbar, ToolbarButton} from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import type {CategorizationTableSettings} from "../settings/CategorizationTableSettings.ts";
import {CATEGORIZATION_TABLE_DENSITY_OPTIONS} from "../settings/CategorizationTableSettings.ts";
import {
    ColumnsPanelTrigger,
    ExportCsv,
    ExportExcel,
    ExportPrint,
    FilterPanelTrigger,
    GridFilterListIcon,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import {FileDownload, Refresh, ViewColumn} from '@mui/icons-material';
import {observer} from "mobx-react-lite";
import {ToolbarQuickFilters} from "./ToolbarQuickFilters.tsx";
import {ToolbarToggleGroupBy} from "./ToolbarToggleGroupBy.tsx";
import {ToolbarFilterChips} from "./ToolbarFilterChips.tsx";
import {useStores} from "../../../../../../stores";
import {ToolbarAlerts} from "./ToolbarAlerts.tsx";
import {ToolbarLoadingIndicator} from "./ToolbarLoadingIndicator.tsx";
import {ToolbarMoreMenu} from "./ToolbarMoreMenu.tsx";


type Props = {
    settings: CategorizationTableSettings;
    onSettingsChange: React.Dispatch<React.SetStateAction<CategorizationTableSettings>>;
};
export const CategorizationReviewTableToolbar: React.FC<Props> = observer(({settings, onSettingsChange}) => {
    const {p, categorizationReviewStore} = useStores();
    const apiRef = useGridApiContext();

    const [settingsMenuOpen, setSettingsMenuOpen] = React.useState(false);
    const settingsMenuTriggerRef = React.useRef<HTMLButtonElement>(null);

    const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
    const exportMenuTriggerRef = React.useRef<HTMLButtonElement>(null);

    const handleRefreshData = () => {
        // noinspection TypeScriptUnresolvedReference
        apiRef.current?.dataSource?.cache?.clear();
        // noinspection TypeScriptUnresolvedReference
        apiRef.current?.dataSource?.fetchRows();
        categorizationReviewStore.refresh();
    };

    return (
        <Toolbar>

            <ToolbarToggleGroupBy/>

            <Tooltip title="Filters">
                <FilterPanelTrigger render={<ToolbarButton/>}>
                    <GridFilterListIcon fontSize="small"/>
                </FilterPanelTrigger>
            </Tooltip>

            <ToolbarFilterChips/>

            <Stack direction="row" sx={{
                gap: 0.5,
                flex: 1,
                minHeight: '3.5em', // To avoid layout shift when alert appears
            }}>
                <ToolbarAlerts/>
            </Stack>

            <ToolbarLoadingIndicator/>

            <ToolbarMoreMenu/>

            <ToolbarQuickFilters/>

            <ColumnsPanelTrigger render={<ToolbarButton/>}>
                <ViewColumn fontSize="small"/>
            </ColumnsPanelTrigger>

            {p.p.showExportInCategorizationTable && <>
                {/*Experimental feature, download is not in a nice format*/}
                <Tooltip title="Export">
                    <ToolbarButton
                        ref={exportMenuTriggerRef}
                        id="export-menu-trigger"
                        aria-controls="export-menu"
                        aria-haspopup="true"
                        aria-expanded={exportMenuOpen ? 'true' : undefined}
                        onClick={() => setExportMenuOpen(true)}
                    >
                        <FileDownload fontSize="small"/>
                    </ToolbarButton>
                </Tooltip>
                <Menu
                    id="export-menu"
                    anchorEl={exportMenuTriggerRef.current}
                    open={exportMenuOpen}
                    onClose={() => setExportMenuOpen(false)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    slotProps={{list: {'aria-labelledby': 'export-menu-trigger'}}}
                >
                    <ExportPrint render={<MenuItem/>} onClick={() => setExportMenuOpen(false)}>
                        Print
                    </ExportPrint>
                    <ExportCsv
                        render={<MenuItem/>}
                        onClick={() => setExportMenuOpen(false)}
                        disabled={categorizationReviewStore.reviewPageController.isGroupBy}
                    >
                        Download as CSV
                    </ExportCsv>
                    <ExportExcel
                        render={<MenuItem/>}
                        onClick={() => setExportMenuOpen(false)}
                        disabled={categorizationReviewStore.reviewPageController.isGroupBy}
                        options={{
                            exceljsPostProcess: categorizationReviewStore.reviewPageController.exceljsPostProcess
                        }}>
                        Download as Excel
                    </ExportExcel>
                </Menu>
            </>}

            {CATEGORIZATION_TABLE_DENSITY_OPTIONS.length > 0 && <>
                <Tooltip title="Settings">
                    <ToolbarButton
                        ref={settingsMenuTriggerRef}
                        id="settings-menu-trigger"
                        aria-controls="settings-menu"
                        aria-haspopup="true"
                        aria-expanded={settingsMenuOpen ? 'true' : undefined}
                        onClick={() => setSettingsMenuOpen(true)}
                    >
                        <SettingsIcon fontSize="small" sx={{ml: 'auto'}}/>
                    </ToolbarButton>
                </Tooltip>
                <Menu
                    id="settings-menu"
                    anchorEl={settingsMenuTriggerRef.current}
                    open={settingsMenuOpen}
                    onClose={() => setSettingsMenuOpen(false)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    slotProps={{
                        list: {
                            'aria-labelledby': 'settings-menu-trigger',
                        },
                    }}
                >
                    {CATEGORIZATION_TABLE_DENSITY_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.value}
                            onClick={() =>
                                onSettingsChange((currentSettings) => ({
                                    ...currentSettings,
                                    density: option.value,
                                }))
                            }
                        >
                            <ListItemIcon>
                                {settings.density === option.value && <CheckIcon fontSize="small"/>}
                            </ListItemIcon>
                            <ListItemText>{option.label}</ListItemText>
                        </MenuItem>
                    ))}
                    <Divider/>
                    {/*<MenuItem*/}
                    {/*    onClick={() =>*/}
                    {/*        onSettingsChange((currentSettings) => ({*/}
                    {/*            ...currentSettings,*/}
                    {/*            showColumnBorders: !currentSettings.showColumnBorders,*/}
                    {/*        }))*/}
                    {/*    }*/}
                    {/*>*/}
                    {/*    <ListItemIcon>*/}
                    {/*        {settings.showColumnBorders && <CheckIcon fontSize="small" />}*/}
                    {/*    </ListItemIcon>*/}
                    {/*    <ListItemText>Show column borders</ListItemText>*/}
                    {/*</MenuItem>*/}
                    {/*<MenuItem*/}
                    {/*    onClick={() =>*/}
                    {/*        onSettingsChange((currentSettings) => ({*/}
                    {/*            ...currentSettings,*/}
                    {/*            showCellBorders: !currentSettings.showCellBorders,*/}
                    {/*        }))*/}
                    {/*    }*/}
                    {/*>*/}
                    {/*    <ListItemIcon>*/}
                    {/*        {settings.showCellBorders && <CheckIcon fontSize="small" />}*/}
                    {/*    </ListItemIcon>*/}
                    {/*    <ListItemText>Show cell borders</ListItemText>*/}
                    {/*</MenuItem>*/}
                </Menu>
            </>}

            <Tooltip title="Refresh data">
                <ToolbarButton onClick={handleRefreshData}>
                    <Refresh fontSize="small"/>
                </ToolbarButton>
            </Tooltip>
        </Toolbar>
    );
})
